import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import {
    Announcement,
    AnnouncementService
} from '../../../services/announcement/announcement.service';
import { WindowReferenceService } from '../../../services/window-reference/window-reference.service';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';
import { Utilities } from '../../../utilities/utilities.helper';

@LifecycleAware()
@Component({
    selector: 'lsq-announcement-row',
    template: `
        <div
            class="lsq-announcement-row"
            [ngClass]="{ clickable: announcement.onClick }"
            (click)="handleClick($event)"
            (mouseenter)="handleMouseEnter()"
            (mouseleave)="handleMouseleave()"
        >
            <div class="container-fluid page-container">
                <lsq-icon
                    [type]="announcement.icon ? announcement.icon : icons.Bell"
                    [color]="color.Black"
                    height="20"
                    width="20"
                ></lsq-icon>
                <div
                    #announcementBanner
                    class="announcement-contents"
                    [ngClass]="{ dismissable: announcement.dismissable }"
                >
                    <span *ngIf="doContentsFit">
                        <div
                            class="announcement-text-container"
                            [@ticker]="tickerScrolling ? 'scroll-end' : 'scroll-start'"
                        >
                            <div
                                #announcementBannerText
                                class="announcement-text"
                                [innerHTML]="announcement.text"
                            ></div>
                        </div>
                    </span>
                    <span *ngIf="!doContentsFit">
                        <div
                            class="announcement-text-container"
                            [ngClass]="{ 'show-ellipsis': !tickerScrolling }"
                            [@ticker]="tickerScrolling ? 'scroll-end' : 'scroll-start'"
                        >
                            <div
                                class="announcement-text overflowing"
                                [innerHTML]="announcement.text"
                            ></div>
                            <div
                                class="announcement-text overflowing"
                                [innerHTML]="announcement.text"
                            ></div>
                        </div>
                    </span>
                </div>
                <lsq-icon
                    *ngIf="announcement.dismissable"
                    (click)="closeAnnouncement()"
                    class="close-button"
                    width="20"
                    [type]="icons.CloseSmall"
                    [color]="color.Black"
                >
                </lsq-icon>
            </div>
        </div>
    `,
    styleUrls: ['./lsq-announcement.component.scss'],
    animations: [
        trigger('ticker', [
            state(
                'scroll-start',
                style({
                    transform: 'translateX(0%)'
                })
            ),
            state(
                'scroll-end',
                style({
                    transform: 'translateX(-50%)'
                })
            ),
            transition('scroll-start => scroll-end', [animate('20s')])
        ])
    ]
})
export class AnnouncementRowComponent implements AfterViewInit, OnDestroy {
    @Input() public announcement: Announcement;
    @ViewChild('announcementBanner') public announcementBanner: ElementRef;
    @ViewChild('announcementBannerText') public announcementBannerText: ElementRef;

    public icons = Icons;
    public color = Color;
    public tickerScrolling = false;
    public mouseArmed = false;
    public doContentsFit = true;

    private _hoverGracePeriod = 600;
    private _startTickerDelay = 5000;
    private _animationTime = 20000;

    constructor(
        private _windowReferenceService: WindowReferenceService,
        private _announcementService: AnnouncementService
    ) {}

    public ngAfterViewInit(): void {
        this.delayTicker();

        this._windowReferenceService.resizeStream.pipe(forLifeOf(this)).subscribe(() => {
            this.tickerScrolling = false;
            this.doContentsFit = true;

            Utilities.executeAfterRender(() => {
                if (this.announcementBanner && this.announcementBannerText) {
                    this.doContentsFit =
                        this.announcementBanner.nativeElement.getBoundingClientRect().width >
                        this.announcementBannerText.nativeElement.getBoundingClientRect().width;
                }
            });
        });
    }

    public ngOnDestroy(): void {}

    public handleClick(event: Event): void {
        if (this.announcement.onClick) {
            this.announcement.onClick(event);
        }
    }

    public handleMouseEnter(): void {
        this.mouseArmed = true;
        if (!this.doContentsFit) {
            setTimeout(() => {
                if (this.mouseArmed && !this.tickerScrolling) {
                    this.toggleTicker();
                }
            }, this._hoverGracePeriod);
        }
    }

    public handleMouseleave(): void {
        this.mouseArmed = false;
    }

    public delayTicker(): void {
        setTimeout(() => {
            this.toggleTicker();
        }, this._startTickerDelay);
    }

    public toggleTicker(): void {
        if (!this.doContentsFit) {
            this.tickerScrolling = !this.tickerScrolling;
            if (this.tickerScrolling) {
                setTimeout(() => {
                    this.toggleTicker();
                }, this._animationTime);
            }
        } else {
            this.tickerScrolling = false;
        }
    }

    public closeAnnouncement(): void {
        this._announcementService.removeAnnouncement(this.announcement.id);
    }
}
